import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FilePlus2, FileText, BookOpen, User, Shield, LogOut, Clock, AlertTriangle, LayoutDashboard, Wrench, CalendarDays, ClipboardCheck, Sparkles, ChevronDown, X, LayoutGrid } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { isDashboardEnabled } from "../config/features";
import { hasPro, PLANNER_ROLES } from "../lib/pro";

function daysLeft(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 86400000));
}

function TrialBanner() {
  const nav = useNavigate();
  const { t } = useLang();
  const [b, setB] = useState(null);
  useEffect(() => { api.get("/billing/status").then(({ data }) => setB(data)).catch(() => {}); }, []);
  if (!b) return null;
  const isTrial = b.status === "trialing";
  const isPastDue = b.status === "past_due";
  if (!isTrial && !isPastDue) return null;
  const left = daysLeft(b.trial_ends_at);
  return (
    <div data-testid="trial-banner"
      className={`w-full text-sm font-semibold px-4 py-2.5 flex items-center justify-center gap-2 ${isPastDue ? "bg-red-500/15 text-red-500" : "bg-[#F26722]/12 text-[#F26722]"}`}>
      {isPastDue ? <AlertTriangle size={15} /> : <Clock size={15} />}
      <span>
        {isPastDue ? t("Your last payment failed.") : left === 0 ? t("Your free trial ends today.") : `${t("Your free trial ends in")} ${left} ${left === 1 ? t("day") : t("days")}.`}
      </span>
      {b.can_manage && (
        <button onClick={() => nav("/subscribe")} data-testid="trial-banner-subscribe" className="underline underline-offset-2 hover:opacity-80">
          {t("Subscribe now")}
        </button>
      )}
    </div>
  );
}

export function CpmLogo({ className = "" }) {
  return (
    <div className={`cpm-logo ${className}`} data-testid="cpm-logo">
      <span>CPM</span>
    </div>
  );
}

// Company-aware logo mark. CPM users see the CPM oval; everyone else sees the generic RV mark.
export function BrandMark({ company }) {
  const auth = useAuth();
  const resolved = company !== undefined ? company : auth?.user?.company_name;
  if (resolved === "CPM") return <CpmLogo />;
  return (
    <div className="rv-logo" data-testid="brand-mark-arv">
      <span className="rv-logo-mark">RV</span>
      <span className="rv-logo-tail">
        <span className="rv-logo-bar" />
        <span className="rv-logo-sub">Service Tool</span>
      </span>
    </div>
  );
}

const tabs = [
  { to: "/new", label: "New Report", icon: FilePlus2, id: "tab-new", hideForOffice: true },
  { to: "/reports", label: "Reports", icon: FileText, id: "tab-reports" },
  { to: "/planning", label: "Planning", mechLabel: "My jobs", desc: "Plan jobs for your mechanics", mechDesc: "Your planned jobs", icon: CalendarDays, id: "tab-planning", pro: true, group: "pro" },
  { to: "/dashboard", label: "Dashboard", desc: "Your company at a glance", icon: LayoutDashboard, id: "tab-dashboard", managerOffice: true, dashboard: true, group: "pro" },
  { to: "/checklists", label: "Checklists", desc: "One checklist template per machine type", icon: ClipboardCheck, id: "tab-checklists", managerOffice: true, pro: true, group: "pro" },
  { to: "/parts", label: "Parts Book", desc: "Search parts and drawings", icon: BookOpen, id: "tab-parts-book", group: "more" },
  { to: "/machines", label: "Machines", desc: "Machine history by serial number", icon: Wrench, id: "tab-machines", group: "more" },
  { to: "/profile", label: "Profile", desc: "Your account and company settings", icon: User, id: "tab-profile", group: "more" },
];

function visibleTabs(user) {
  return tabs.filter((t) => {
    if (t.dashboard && !isDashboardEnabled(user)) return false;
    if (t.pro && !hasPro(user)) return false;
    if (t.hideForOffice && user?.role === "office") return false;
    if (t.managerOffice && !PLANNER_ROLES.includes(user?.role)) return false;
    return true;
  });
}

const tabLabel = (t, user) => (t.mechLabel && !PLANNER_ROLES.includes(user?.role) ? t.mechLabel : t.label);
const tabDesc = (t, user) => (t.mechDesc && !PLANNER_ROLES.includes(user?.role) ? t.mechDesc : t.desc);

// Mechanics: number of today's jobs that are not done yet, refreshed on navigation and every 5 minutes.
function useTodayJobs(user) {
  const { pathname } = useLocation();
  const [count, setCount] = useState(0);
  const enabled = hasPro(user) && user?.role === "mechanic";
  useEffect(() => {
    if (!enabled) return;
    const d = new Date();
    const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const load = () => api.get("/pro/jobs", { params: { start: today, end: today } }).then(({ data }) => setCount(data.filter((j) => j.status !== "done").length)).catch(() => setCount(0));
    load();
    const id = setInterval(load, 300000);
    return () => clearInterval(id);
  }, [enabled, pathname]);
  return enabled ? count : 0;
}

function Badge({ n, testid, className = "" }) {
  if (!n) return null;
  return <span data-testid={testid} style={{ background: "#F26722", color: "#fff" }} className={`min-w-[18px] h-[18px] px-1 rounded-full bg-[#F26722] text-white text-[10px] font-black flex items-center justify-center ${className}`}>{n}</span>;
}

// Main tabs with the Pro group collapsed into one "pro" placeholder after Reports.
function useNavItems(user) {
  const all = visibleTabs(user);
  const pro = all.filter((t) => t.group === "pro");
  const more = all.filter((t) => t.group === "more");
  const main = all.filter((t) => t.group !== "pro");
  if (pro.length) main.splice(main.findIndex((t) => t.to === "/reports") + 1, 0, { pro: true, id: "tab-pro" });
  return { main, pro, more };
}

function ProItems({ items, user, onPick, badge }) {
  const { t: tr } = useLang();
  return items.map((t) => (
    <NavLink key={t.to} to={t.to} onClick={onPick} data-testid={`pro-menu-${t.id}`}
      className={({ isActive }) => `flex items-start gap-3 px-3 py-2.5 rounded-xl transition-colors ${isActive ? "bg-[#F26722]/10" : "hover:bg-zinc-800/60"}`}>
      <span className="w-9 h-9 rounded-lg bg-[#F26722]/15 flex items-center justify-center shrink-0"><t.icon size={18} className="text-[#F26722]" /></span>
      <span className="leading-tight flex-1"><span className="block text-sm font-bold text-white">{tr(tabLabel(t, user))}</span><span className="block text-xs text-zinc-500 mt-0.5">{tr(tabDesc(t, user))}</span></span>
      {t.to === "/planning" && <Badge n={badge} testid="pro-menu-today-badge" className="mt-1" />}
    </NavLink>
  ));
}

function NavMenu({ items, user, label, icon: Icon, testid, heading, badge }) {
  const { t: tr } = useLang();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const active = items.some((t) => pathname.startsWith(t.to));
  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", close); document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", esc); };
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} data-testid={`${testid}-btn`} aria-expanded={open}
        className={`px-3.5 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-1.5 ${active || open ? "text-[#F26722] bg-[#F26722]/10" : "text-zinc-400 hover:text-white hover:bg-zinc-800/60"}`}>
        <Icon size={15} /> {label} <Badge n={badge} testid={`${testid}-badge`} /> <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-2 w-72 bg-[#111113] border border-zinc-800 rounded-2xl p-2 shadow-2xl fade-up" data-testid={testid}>
          <p className="text-[10px] font-bold tracking-wider text-zinc-500 uppercase px-3 pt-1 pb-2">{tr(heading)}</p>
          <ProItems items={items} user={user} badge={badge} onPick={() => setOpen(false)} />
        </div>
      )}
    </div>
  );
}

function ProSheet({ items, user, onClose, badge }) {
  const { t: tr } = useLang();
  return createPortal(
    <div className="surface-dark md:hidden fixed inset-0 z-[60] bg-black/60" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="mobile-pro-sheet" className="absolute bottom-0 left-0 right-0 max-w-md mx-auto bg-[#111113] border-t border-zinc-800 rounded-t-2xl p-3 pb-24 fade-up">
        <div className="flex items-center justify-between px-3 py-2">
          <p className="text-[11px] font-bold tracking-wider text-zinc-500 uppercase">{tr("Pro package")}</p>
          <button onClick={onClose} data-testid="mobile-pro-sheet-close" className="text-zinc-500 hover:text-white"><X size={20} /></button>
        </div>
        <ProItems items={items} user={user} badge={badge} onPick={onClose} />
      </div>
    </div>,
    document.body
  );
}

function initialsOf(name) {
  return (name || "?").split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase();
}

function TopNav() {
  const { user, logout } = useAuth();
  const { t: tr } = useLang();
  const nav = useNavigate();
  const { main, pro, more } = useNavItems(user);
  const today = useTodayJobs(user);
  return (
    <header className="surface-dark hidden md:block sticky top-0 z-50 backdrop-blur-xl border-b border-zinc-800/70">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-8">
          <button onClick={() => nav("/")} className="flex items-center" data-testid="nav-logo">
            <BrandMark company={null} />
          </button>
          <nav className="flex items-center gap-1">
            {main.filter((t) => t.group !== "more").map((t) => t.pro ? <NavMenu key="pro" items={pro} user={user} label="Pro" icon={Sparkles} testid="nav-pro-menu" heading="Pro package" badge={today} /> : (
              <NavLink key={t.to} to={t.to} data-testid={`nav-${t.id}`}
                className={({ isActive }) => `px-3.5 py-2 rounded-lg text-sm font-semibold transition-colors ${isActive ? "text-[#F26722] bg-[#F26722]/10" : "text-zinc-400 hover:text-white hover:bg-zinc-800/60"}`}>
                {tr(tabLabel(t, user))}
              </NavLink>
            ))}
            {more.length > 0 && <NavMenu items={more} user={user} label={tr("More")} icon={LayoutGrid} testid="nav-more-menu" heading="More" />}
            {user?.role === "super_admin" && (
              <NavLink to="/admin" data-testid="nav-tab-admin"
                className={({ isActive }) => `px-3.5 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-1.5 ${isActive ? "text-amber-400 bg-amber-500/10" : "text-amber-500/80 hover:text-amber-400 hover:bg-amber-500/10"}`}>
                <Shield size={15} /> {tr("Admin")}
              </NavLink>
            )}
          </nav>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right hidden lg:block leading-tight">
            <p className="text-sm font-semibold text-white">{user?.name}</p>
            <p className="text-[11px] text-zinc-500">{user?.role} {user?.company_name ? `· ${user.company_name}` : ""}</p>
          </div>
          <button onClick={() => nav("/profile")} data-testid="nav-avatar-profile" title={tr("Profile")} className="w-9 h-9 rounded-lg bg-[#F26722] text-white flex items-center justify-center font-black text-sm hover:opacity-90 transition-opacity">{initialsOf(user?.name)}</button>
          <button onClick={logout} data-testid="nav-logout-btn" className="text-zinc-400 hover:text-red-400 p-2 rounded-lg hover:bg-zinc-800/60 transition-colors" title={tr("Log out")}><LogOut size={18} /></button>
        </div>
      </div>
    </header>
  );
}

function SiteFooter() {
  const { t } = useLang();
  return (
    <footer className="surface-dark hidden md:block border-t border-zinc-800/60 mt-12">
      <div className="max-w-6xl mx-auto px-6 py-8 flex items-center justify-between text-sm text-zinc-500">
        <BrandMark company={null} />
        <div className="flex items-center gap-6">
          <a href="mailto:info@rvservicetool.nl" className="hover:text-zinc-200 transition-colors" data-testid="app-footer-email">info@rvservicetool.nl</a>
          <NavLink to="/privacy" className="hover:text-zinc-200 transition-colors" data-testid="app-footer-privacy">{t("Privacy policy")}</NavLink>
          <NavLink to="/terms" className="hover:text-zinc-200 transition-colors" data-testid="app-footer-terms">{t("Terms and conditions")}</NavLink>
          <p>© {new Date().getFullYear()} RV Service Tool — {t("Field service reporting")}</p>
        </div>
      </div>
    </footer>
  );
}

export function PageHeader({ title, subtitle, right }) {
  return (
    <div className="sticky top-0 md:relative z-40 bg-[#0A0A0A]/90 backdrop-blur-md px-4 md:px-0 py-4 md:pt-8 md:pb-4 border-b border-zinc-800/70 md:border-0 flex items-start justify-between">
      <div>
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white tracking-tight" data-testid="page-title">{title}</h1>
        {subtitle && <p className="text-xs sm:text-sm text-zinc-400 mt-0.5 md:mt-1">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-2 md:hidden">{right}<BrandMark /></div>
      {right && <div className="hidden md:block">{right}</div>}
    </div>
  );
}

export function BottomTabBar() {
  const { user } = useAuth();
  const { t: tr } = useLang();
  const { main, pro } = useNavItems(user);
  const today = useTodayJobs(user);
  const [sheet, setSheet] = useState(false);
  const { pathname } = useLocation();
  const proActive = pro.some((t) => pathname.startsWith(t.to));
  return (
    <div className="surface-dark md:hidden fixed bottom-0 left-0 right-0 z-50 backdrop-blur-xl border-t border-zinc-800/80 px-2 py-2 max-w-md mx-auto flex justify-around items-center">
      {main.map((t) => t.pro ? (
        <button key="pro" onClick={() => setSheet(true)} data-testid="tab-pro"
          className={`relative flex flex-col items-center gap-1 text-[11px] font-semibold transition-colors px-1.5 py-1 ${proActive || sheet ? "text-[#F26722]" : "text-zinc-500 hover:text-zinc-300"}`}>
          <Sparkles size={22} strokeWidth={2.2} /> Pro
          <Badge n={today} testid="tab-pro-badge" className="absolute -top-1 right-0" />
        </button>
      ) : (
        <NavLink key={t.to} to={t.to} data-testid={t.id}
          className={({ isActive }) => `flex flex-col items-center gap-1 text-[11px] font-semibold transition-colors px-1.5 py-1 ${isActive ? "text-[#F26722]" : "text-zinc-500 hover:text-zinc-300"}`}>
          <t.icon size={22} strokeWidth={2.2} />
          {t.label === "New Report" ? tr("New") : tr(tabLabel(t, user))}
        </NavLink>
      ))}
      {user?.role === "super_admin" && (
        <NavLink to="/admin" data-testid="tab-admin" className="flex flex-col items-center gap-1 text-[11px] font-semibold text-amber-500 hover:text-amber-400 transition-colors px-3 py-1">
          <Shield size={22} strokeWidth={2.2} /> {tr("Admin")}
        </NavLink>
      )}
      {sheet && <ProSheet items={pro} user={user} badge={today} onClose={() => setSheet(false)} />}
    </div>
  );
}

export default function Layout({ children }) {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col">
      <TopNav />
      <TrialBanner />
      <main className="flex-1 w-full max-w-md md:max-w-6xl mx-auto px-0 md:px-6 pb-24 md:pb-4">
        <div className="fade-up">{children}</div>
      </main>
      <SiteFooter />
      <BottomTabBar />
    </div>
  );
}
